import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Link} from "react-router-dom";
import {Dropdown} from "primereact/dropdown";
import {Calendar} from "primereact/calendar";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {Chart} from "primereact/chart";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {setCreateButton, setTitle} from "../actions/main";
import {sendRequest} from "../services/Sender";
import formater from "../helpers/formater";
import Loader from "../components/Loader";

// Кухня по артистам: рейтинг за период + сравнение с другим периодом.
// Данные кухни — из R-Keeper (зал мероприятия), билеты — из карточек мероприятий.

const fmt = (d) => {
    const z = (n) => String(n).padStart(2, '0')
    return `${d.getFullYear()}-${z(d.getMonth() + 1)}-${z(d.getDate())}`
}
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x }
const yesterday = () => addDays(new Date(new Date().toDateString()), -1)

const PRESETS = [
    {label: 'Этот месяц', value: 'this_month'},
    {label: 'Прошлый месяц', value: 'last_month'},
    {label: 'Этот квартал', value: 'this_quarter'},
    {label: 'Прошлый квартал', value: 'last_quarter'},
    {label: 'Этот год', value: 'this_year'},
    {label: 'Прошлый год', value: 'last_year'},
    {label: 'Последние 12 месяцев', value: 'last_12m'},
    {label: 'Свой период', value: 'custom'},
]
const COMPARE = [
    {label: 'Без сравнения', value: 'none'},
    {label: 'Предыдущий период', value: 'prev'},
    {label: 'Тот же период год назад', value: 'yoy'},
    {label: 'Свой период', value: 'custom'},
]
const SORTS = [
    {label: 'по сумме кухни', value: 'kitchen'},
    {label: 'по кухне за вечер', value: 'kitchen_avg'},
    {label: 'по кухне на билет', value: 'kitchen_per_ticket'},
]

function presetRange(p) {
    const t = new Date(new Date().toDateString())
    const y = yesterday()
    const q = Math.floor(t.getMonth() / 3)
    switch (p) {
        case 'this_month': return [new Date(t.getFullYear(), t.getMonth(), 1), y]
        case 'last_month': return [new Date(t.getFullYear(), t.getMonth() - 1, 1), new Date(t.getFullYear(), t.getMonth(), 0)]
        case 'this_quarter': return [new Date(t.getFullYear(), q * 3, 1), y]
        case 'last_quarter': return [new Date(t.getFullYear(), q * 3 - 3, 1), new Date(t.getFullYear(), q * 3, 0)]
        case 'this_year': return [new Date(t.getFullYear(), 0, 1), y]
        case 'last_year': return [new Date(t.getFullYear() - 1, 0, 1), new Date(t.getFullYear() - 1, 11, 31)]
        case 'last_12m': return [addDays(new Date(t.getFullYear() - 1, t.getMonth(), t.getDate()), 0), y]
        default: return null
    }
}

function compareRange(mode, [d1, d2]) {
    if (mode === 'prev') {
        const len = Math.round((d2 - d1) / 86400000) + 1
        return [addDays(d1, -len), addDays(d1, -1)]
    }
    if (mode === 'yoy') {
        const shift = (d) => new Date(d.getFullYear() - 1, d.getMonth(), d.getDate())
        return [shift(d1), shift(d2)]
    }
    return null
}

const money = (v) => (v === null || v === undefined ? '—' : formater.amountFormat(v))
const pctTpl = (v) => {
    if (v === null || v === undefined) return <span style={{color: '#aaa'}}>—</span>
    const up = v >= 0
    return <span style={{color: up ? '#1a9b5b' : '#d64545', fontWeight: 600}}>{up ? '▲' : '▼'} {Math.abs(v).toFixed(0)}%</span>
}
const pct = (cur, prev) => (cur === null || cur === undefined || !prev ? null : (cur - prev) / prev * 100)

function KitchenArtists() {
    const dispatch = useDispatch()
    const currentDepartment = useSelector((state) => state.currentDepartment)

    const [preset, setPreset] = useState(() => localStorage.getItem('ka_preset') || 'this_year')
    const [range, setRange] = useState(() => presetRange(localStorage.getItem('ka_preset') || 'this_year'))
    const [cmpMode, setCmpMode] = useState(() => localStorage.getItem('ka_cmp') || 'yoy')
    const [cmpRange, setCmpRange] = useState(null)
    const [sortBy, setSortBy] = useState('kitchen')
    const [minEvents, setMinEvents] = useState(1)
    const [search, setSearch] = useState('')
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)

    useEffect(() => {
        dispatch(setTitle('Кухня по артистам'))
        dispatch(setCreateButton(false))
    }, [dispatch])

    const effCmp = useMemo(() => {
        if (!range || !range[0] || !range[1] || cmpMode === 'none') return null
        if (cmpMode === 'custom') return cmpRange && cmpRange[0] && cmpRange[1] ? cmpRange : null
        return compareRange(cmpMode, range)
    }, [range, cmpMode, cmpRange])

    useEffect(() => {
        if (!range || !range[0] || !range[1]) return
        localStorage.setItem('ka_preset', preset)
        localStorage.setItem('ka_cmp', cmpMode)
        const params = {date_from: fmt(range[0]), date_to: fmt(range[1])}
        if (effCmp) { params.cmp_from = fmt(effCmp[0]); params.cmp_to = fmt(effCmp[1]) }
        if (currentDepartment) params.client_id = currentDepartment
        setLoading(true); setError(null)
        sendRequest('analytics/kitchen-by-artist', 'GET', params, true)
            .then((res) => { setData(res); setLoading(false) })
            .catch((e) => { setError(String(e.message || e)); setLoading(false) })
    }, [range, effCmp, currentDepartment, preset, cmpMode])

    const onPreset = (p) => {
        setPreset(p)
        if (p !== 'custom') setRange(presetRange(p))
    }

    const hasCmp = !!(data && data.compare)
    const rows = useMemo(() => {
        if (!data) return []
        let r = data.items.filter((a) => a.events_with_kitchen >= minEvents)
        if (search.trim()) r = r.filter((a) => a.name.toLowerCase().includes(search.trim().toLowerCase()))
        const val = (a) => (a[sortBy] === null || a[sortBy] === undefined ? -1 : a[sortBy])
        r = [...r].sort((a, b) => val(b) - val(a))
        return r.map((a, i) => ({...a, place: i + 1, cmp_val: a.cmp ? a.cmp[sortBy] : null}))
    }, [data, minEvents, search, sortBy])

    const chartData = useMemo(() => {
        const top = rows.filter((a) => a[sortBy] !== null).slice(0, 10)
        const ds = [{label: range ? `${fmt(range[0])} — ${fmt(range[1])}` : 'Период', backgroundColor: '#7c2d12', data: top.map((a) => Math.round(a[sortBy] || 0))}]
        if (hasCmp) ds.push({label: `${data.compare.from} — ${data.compare.to}`, backgroundColor: '#e0b49a', data: top.map((a) => Math.round(a.cmp_val || 0))})
        return {labels: top.map((a) => a.name.length > 28 ? a.name.slice(0, 27) + '…' : a.name), datasets: ds}
    }, [rows, sortBy, hasCmp, data, range])

    const chartOptions = {
        indexAxis: 'y',
        maintainAspectRatio: false,
        plugins: {
            legend: {position: 'bottom'},
            datalabels: {display: false},
            tooltip: {callbacks: {label: (c) => `${c.dataset.label}: ${formater.amountFormat(c.raw)}`}},
        },
        scales: {x: {ticks: {callback: (v) => (v >= 1000000 ? (v / 1000000).toFixed(1) + ' млн' : Math.round(v / 1000) + ' тыс')}}},
    }

    const T = data && data.period.totals
    const C = data && data.compare && data.compare.totals
    const card = (title, cur, prev, isMoney = true) => (
        <div className="col-md-3 col-6 mb-3">
            <div className="card h-100"><div className="card-body">
                <div style={{color: '#888', fontSize: 13}}>{title}</div>
                <h3 style={{margin: '4px 0'}}>{cur === null || cur === undefined ? '—' : (isMoney ? formater.amountFormat(cur) : cur)}</h3>
                {C && <div style={{fontSize: 13}}>{pctTpl(pct(cur, prev))} <span style={{color: '#888'}}>было {prev === null || prev === undefined ? '—' : (isMoney ? formater.amountFormat(prev) : prev)}</span></div>}
            </div></div>
        </div>
    )

    const missingKitchen = T ? T.events - T.events_with_kitchen : 0

    return (
        <div className="container-fluid">
            <div className="card mb-3"><div className="card-body">
                <div className="row align-items-end">
                    <div className="col-md-3 mb-2">
                        <label>Период</label>
                        <Dropdown value={preset} options={PRESETS} onChange={(e) => onPreset(e.value)} style={{width: '100%'}}/>
                    </div>
                    <div className="col-md-3 mb-2">
                        <label>Даты</label>
                        <Calendar value={range} selectionMode="range" dateFormat="dd.mm.yy" readOnlyInput showIcon style={{width: '100%'}}
                                  onChange={(e) => { setPreset('custom'); setRange(e.value) }}/>
                    </div>
                    <div className="col-md-3 mb-2">
                        <label>Сравнить с</label>
                        <Dropdown value={cmpMode} options={COMPARE} onChange={(e) => setCmpMode(e.value)} style={{width: '100%'}}/>
                    </div>
                    <div className="col-md-3 mb-2">
                        {cmpMode === 'custom' ? (<>
                            <label>Даты сравнения</label>
                            <Calendar value={cmpRange} selectionMode="range" dateFormat="dd.mm.yy" readOnlyInput showIcon style={{width: '100%'}}
                                      onChange={(e) => setCmpRange(e.value)}/>
                        </>) : (effCmp && <div style={{color: '#888', paddingBottom: 8}}>
                            Сравнение: {fmt(effCmp[0])} — {fmt(effCmp[1])}</div>)}
                    </div>
                </div>
            </div></div>

            {loading && <Loader/>}
            {error && <div className="alert alert-danger">Ошибка загрузки: {error}</div>}

            {!loading && data && <>
                <div className="row">
                    {card('Кухня за период', T.kitchen, C && C.kitchen)}
                    {card('Кухня за вечер (в среднем)', T.kitchen_avg, C && C.kitchen_avg)}
                    {card('Мероприятий', T.events, C && C.events, false)}
                    {card('Бар за период', T.bar, C && C.bar)}
                </div>

                {missingKitchen > 0 && <div className="alert alert-warning">
                    У {missingKitchen} из {T.events} мероприятий нет данных кухни — смена ещё не выгружена из R-Keeper
                    или мероприятие раньше начала синхронизации.
                </div>}
                {C && C.events_with_kitchen < C.events && <div className="alert alert-warning">
                    В периоде сравнения у {C.events - C.events_with_kitchen} из {C.events} мероприятий нет данных кухни — сравнение неполное.
                </div>}

                <div className="card mb-3"><div className="card-body">
                    <div className="row align-items-end mb-2">
                        <div className="col-md-4"><h4 style={{margin: 0}}>Топ-10 {SORTS.find((s) => s.value === sortBy).label}</h4></div>
                        <div className="col-md-3"><label>Рейтинг</label>
                            <Dropdown value={sortBy} options={SORTS} onChange={(e) => setSortBy(e.value)} style={{width: '100%'}}/></div>
                        <div className="col-md-2"><label>Мин. мероприятий</label>
                            <InputNumber value={minEvents} onValueChange={(e) => setMinEvents(e.value || 1)} min={1} showButtons style={{width: '100%'}}/></div>
                        <div className="col-md-3"><label>Поиск артиста</label>
                            <InputText value={search} onChange={(e) => setSearch(e.target.value)} style={{width: '100%'}}/></div>
                    </div>
                    <div style={{height: 420}}><Chart type="bar" data={chartData} options={chartOptions} style={{height: '100%'}}/></div>
                </div></div>

                <div className="card"><div className="card-body">
                    <DataTable value={rows} paginator rows={25} size="small" stripedRows responsiveLayout="scroll"
                               emptyMessage="Нет данных за выбранный период">
                        <Column field="place" header="Место" sortable style={{width: 70}}/>
                        <Column field="name" header="Артист" sortable
                                body={(a) => a.artist_id ? <Link to={'/artist/' + a.artist_id}>{a.name}</Link> : a.name}/>
                        <Column field="events_with_kitchen" header="Мероприятий" sortable/>
                        <Column field="kitchen" header="Кухня" sortable body={(a) => money(a.kitchen)}/>
                        <Column field="kitchen_avg" header="Кухня за вечер" sortable body={(a) => money(a.kitchen_avg)}/>
                        <Column field="bar" header="Бар" sortable body={(a) => money(a.bar)}/>
                        <Column field="tickets" header="Билеты (ср.)" sortable
                                body={(a) => a.events_with_tickets ? `${a.tickets} (${a.tickets_avg})` : '—'}/>
                        <Column field="kitchen_per_ticket" header="Кухня/билет" sortable body={(a) => money(a.kitchen_per_ticket)}/>
                        {hasCmp && <Column header="Кухня (сравн.)" body={(a) => (a.cmp ? money(a.cmp.kitchen) : '—')}/>}
                        {hasCmp && <Column field="delta_kitchen_pct" header="Δ кухня" sortable body={(a) => pctTpl(a.delta_kitchen_pct)}/>}
                        {hasCmp && <Column header="За вечер (сравн.)" body={(a) => (a.cmp ? money(a.cmp.kitchen_avg) : '—')}/>}
                        {hasCmp && <Column field="delta_kitchen_avg_pct" header="Δ за вечер" sortable body={(a) => pctTpl(a.delta_kitchen_avg_pct)}/>}
                        {hasCmp && <Column field="rank_change" header="Место ±" sortable
                                           body={(a) => a.rank_change === null || a.rank_change === undefined ? (a.cmp ? '—' : <span style={{color: '#888'}}>новый</span>)
                                               : a.rank_change === 0 ? '=' : <span style={{color: a.rank_change > 0 ? '#1a9b5b' : '#d64545'}}>{a.rank_change > 0 ? '▲' : '▼'} {Math.abs(a.rank_change)}</span>}/>}
                    </DataTable>
                </div></div>

                {hasCmp && data.only_in_compare.length > 0 && <div className="card mt-3"><div className="card-body">
                    <h5>Выступали только в периоде сравнения ({data.only_in_compare.length})</h5>
                    <div style={{color: '#666'}}>
                        {data.only_in_compare.slice(0, 30).map((a) => `${a.name} — ${money(a.kitchen)}`).join(' · ')}
                    </div>
                </div></div>}
            </>}
        </div>
    )
}

export default KitchenArtists
